'use client'

import {
    createContext,
    lazy,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react'
import { createPortal } from 'react-dom'
import { MdClose, MdOpenInNew } from 'react-icons/md'

/**
 * Páginas de detalhe reaproveitadas dentro do modal. Todas aceitam
 * `{ idOverride, onBack }`: com `idOverride` elas ignoram o `useParams` e, com
 * `onBack`, trocam o "voltar para a lista" por fechar o modal.
 *
 * `lazy()` de verdade: são páginas grandes e o `detail-popup` é importado por telas
 * que talvez nunca abram o modal. Import estático colocaria as 8 no bundle delas.
 */
const LeadDetailPage = lazy(
    () => import('@/pages/admin/sales/leads/lead-detail-page')
)
const DealDetailPage = lazy(
    () => import('@/pages/admin/sales/deals/deal-detail-page')
)
const ConsultationDetailPage = lazy(
    () => import('@/pages/admin/bookings/consultations/consultation-detail-page')
)
const ExamDetailPage = lazy(
    () => import('@/pages/admin/bookings/exams/exam-detail-page')
)
const OrderDetailPage = lazy(
    () => import('@/pages/admin/bookings/orders/order-detail-page')
)
const BillingDetailPage = lazy(
    () => import('@/pages/admin/financial/billings/billing-detail-page')
)
const ClientDetailPage = lazy(
    () => import('@/pages/admin/person/clients/client-detail-page')
)

// ==================== CONTEXT ====================

export type DetailType =
    | 'lead'
    | 'deal'
    | 'consultation'
    | 'exam'
    | 'order'
    | 'billing'
    | 'client'

/** Rótulo usado nos títulos/tooltips. */
const DETAIL_LABELS: Record<DetailType, string> = {
    lead: 'lead',
    deal: 'oportunidade',
    consultation: 'consulta',
    exam: 'pedido de exames',
    order: 'procedimento',
    billing: 'lançamento',
    client: 'cliente',
}

interface DetailPopupContextValue {
    openDetail: (type: DetailType, id: number) => void
}

const DetailPopupContext = createContext<DetailPopupContextValue>({
    openDetail: () => {},
})

export function useDetailPopup() {
    return useContext(DetailPopupContext)
}

/** Renderiza a página de detalhe correspondente ao tipo. */
function DetailContent({
    type,
    id,
    onBack,
}: {
    type: DetailType
    id: number
    onBack: () => void
}) {
    const props = { idOverride: id, onBack }

    switch (type) {
        case 'lead':
            return <LeadDetailPage {...props} />
        case 'deal':
            return <DealDetailPage {...props} />
        case 'consultation':
            return <ConsultationDetailPage {...props} />
        case 'exam':
            return <ExamDetailPage {...props} />
        case 'order':
            return <OrderDetailPage {...props} />
        case 'billing':
            return <BillingDetailPage {...props} />
        case 'client':
            return <ClientDetailPage {...props} />
    }
}

interface DetailPopupProviderProps {
    children: React.ReactNode
    /**
     * Chamado ao fechar o modal. Use para recarregar a listagem por trás: o usuário
     * pode ter excluído ou editado o registro lá dentro, e a linha ficaria fantasma.
     */
    onClose?: () => void
}

export function DetailPopupProvider({
    children,
    onClose,
}: DetailPopupProviderProps) {
    const [detail, setDetail] = useState<{
        type: DetailType
        id: number
    } | null>(null)

    const openDetail = useCallback((type: DetailType, id: number) => {
        setDetail({ type, id })
    }, [])

    const close = useCallback(() => {
        setDetail(null)
        onClose?.()
    }, [onClose])

    // Esc fecha o modal, como em qualquer diálogo da aplicação.
    useEffect(() => {
        if (!detail) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [detail, close])

    return (
        <DetailPopupContext.Provider value={{ openDetail }}>
            {children}
            {detail !== null &&
                createPortal(
                    // NUNCA colocar `data-theme` aqui: o daisyUI dá a todo elemento com
                    // `[data-theme]` o fundo de página (`background-image: linear-gradient(...)`),
                    // e como `.modal` é `fixed inset-0` isso vira uma lâmina OPACA sobre o app.
                    // O tema vem do `<html>` (ver `index.html` e `useTheme`).
                    <div className="modal modal-open">
                        {/* Wrapper posicionado só para ancorar o botão de fechar na quina
                            da caixa. Dentro dela ele colidiria com os botões de ação das
                            páginas (editar/excluir); no canto da tela, com o "Sair". */}
                        <div className="relative w-11/12 max-w-5xl">
                            <button
                                className="btn btn-sm btn-circle absolute -right-3 -top-3 z-20 shadow-lg"
                                onClick={close}
                                aria-label={`Fechar detalhes de ${DETAIL_LABELS[detail.type]}`}
                            >
                                <MdClose size={18} />
                            </button>
                            <div className="modal-box !w-full !max-w-none h-[90vh] p-0 flex flex-col overflow-hidden">
                                <div className="flex-1 overflow-y-auto">
                                    <Suspense
                                        fallback={
                                            <div className="flex items-center justify-center h-full py-20">
                                                <span className="loading loading-spinner loading-lg text-secondary" />
                                            </div>
                                        }
                                    >
                                        {/* `key` força remontar ao trocar de registro: sem
                                        isso a página reaproveita o estado do anterior. */}
                                        <DetailContent
                                            key={`${detail.type}:${detail.id}`}
                                            type={detail.type}
                                            id={detail.id}
                                            onBack={close}
                                        />
                                    </Suspense>
                                </div>
                            </div>
                        </div>
                        <form
                            method="dialog"
                            className="modal-backdrop"
                            onClick={close}
                        >
                            <button>close</button>
                        </form>
                    </div>,
                    document.body
                )}
        </DetailPopupContext.Provider>
    )
}

// ==================== BUTTONS ====================

interface DetailButtonProps {
    type: DetailType
    id: number
    size?: number
    className?: string
    label?: string
}

export function DetailPopupButton({
    type,
    id,
    size = 14,
    className = '',
    label,
}: DetailButtonProps) {
    const { openDetail } = useContext(DetailPopupContext)

    return (
        <button
            className={`btn btn-ghost btn-xs ${label ? '' : 'btn-circle'} ${className}`}
            title={`Ver ${DETAIL_LABELS[type]}`}
            onClick={() => openDetail(type, id)}
        >
            {/* Sem rótulo o botão ficava vazio e invisível (card de lead no funil) */}
            {label ? <span>{label}</span> : <MdOpenInNew size={size} />}
        </button>
    )
}
